import { useEffect, useState } from "react";

/**
 * Hook que gestiona el estado del descuento manual: montos, porcentajes,
 * strings para inputs controlados, validación, y carga desde opportunityData.
 */
export function useDiscountState({ isEditMode, opportunityData, rol }) {
  // --- Estados de descuento ---
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [manualDiscountAmount, setManualDiscountAmount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [originalAmount, setOriginalAmount] = useState(0);
  const [discountMethod, setDiscountMethod] = useState("finalAmount");
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountType, setDiscountType] = useState("none");
  const [forceRecalc, setForceRecalc] = useState(0);

  // --- Strings para inputs controlados ---
  const [finalAmountStr, setFinalAmountStr] = useState("0");
  const [manualDiscountAmountStr, setManualDiscountAmountStr] = useState("0");
  const [discountPercentageStr, setDiscountPercentageStr] = useState("0");

  // --- Valor derivado ---
  const manualDiscountPercentage =
    manualDiscountAmount > 0 && originalAmount > 0 ? (manualDiscountAmount / originalAmount) * 100 : 0;

  // --- Validación de descuento ---
  const validateDiscount = (inversionFinalTotal, discountAmount, finalAmt) => {
    if (!["superadmin", "gerencia", "comercial"].includes(rol)) {
      return "Tu rol no tiene permiso para aplicar descuentos.";
    }

    let maxDiscount;
    if (rol === "superadmin" || rol === "gerencia") {
      maxDiscount = Infinity;
    } else if (rol === "comercial") {
      maxDiscount = inversionFinalTotal * 0.1;
    }

    if (discountAmount > maxDiscount) {
      return rol === "comercial"
        ? "El descuento no puede superar el 10% del monto original."
        : "Error en la validación del descuento.";
    }
    if (finalAmt < 0) {
      return "El monto final no puede ser negativo.";
    }
    if (finalAmt > inversionFinalTotal) {
      return "El monto final no puede ser mayor al monto original.";
    }
    return null;
  };

  // --- Cargar descuento desde opportunityData en modo edición ---
  useEffect(() => {
    if (isEditMode && opportunityData) {
      const hasManualDiscount = opportunityData.has_manual_discount === true;
      const savedDiscountAmount = parseFloat(opportunityData.manual_discount_amount) || 0;
      const savedOriginalAmount = parseFloat(opportunityData.original_amount) || 0;
      const savedPercentage = savedOriginalAmount > 0 ? (savedDiscountAmount / savedOriginalAmount) * 100 : 0;

      setOriginalAmount(savedOriginalAmount);
      setDiscountPercentage(savedPercentage);
      if (hasManualDiscount) {
        setManualDiscountAmount(savedDiscountAmount);
        setFinalAmount(parseFloat(opportunityData.importe) || 0);
        setFinalAmountStr((parseFloat(opportunityData.importe) || 0).toString());
        setManualDiscountAmountStr(savedDiscountAmount.toString());
        setDiscountPercentageStr(savedPercentage.toString());
      }
    }
  }, [isEditMode, opportunityData]);

  // --- Sincronizar strings con valores numéricos ---
  useEffect(() => {
    setFinalAmountStr(finalAmount.toString());
    setManualDiscountAmountStr(manualDiscountAmount.toString());
    setDiscountPercentageStr(discountPercentage.toString());
  }, [finalAmount, manualDiscountAmount, discountPercentage]);

  return {
    // Estado del modal
    showDiscountModal,
    setShowDiscountModal,
    // Valores numéricos
    manualDiscountAmount,
    setManualDiscountAmount,
    finalAmount,
    setFinalAmount,
    originalAmount,
    setOriginalAmount,
    discountPercentage,
    setDiscountPercentage,
    discountType,
    setDiscountType,
    // Método y recálculo
    discountMethod,
    setDiscountMethod,
    forceRecalc,
    setForceRecalc,
    // Strings para inputs
    finalAmountStr,
    setFinalAmountStr,
    manualDiscountAmountStr,
    setManualDiscountAmountStr,
    discountPercentageStr,
    setDiscountPercentageStr,
    // Derivado
    manualDiscountPercentage,
    // Validación
    validateDiscount,
  };
}
